import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';

const Frame245: React.FC = () => {
  const navigate = useNavigate();

  const handleNext = () => {
    navigate('/frame-246');
  };

  const handleBack = () => {
    navigate('/frame-140');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader title="Assign and Approve Asset Distribution" />

        <div className="max-w-4xl space-y-8">
          {/* Introduction */}
          <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              Deciding how your assets should be distributed is a deeply personal step; one that
              helps ensure your wishes are understood and respected. Taking the time to document
              these decisions now can bring clarity, reduce uncertainty, and provide meaningful
              guidance to the people you care about most.
            </p>
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              This isn’t about rushing decisions or finalizing legal documents. It’s about clearly
              expressing your intentions, in your own words, so your loved ones aren’t left
              guessing later.
            </p>
          </div>

          {/* What You'll Do Section */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-xl font-semibold text-cyan-900 mb-4">What You&apos;ll Do</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Assign beneficiaries to your major asset categories
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Indicate how different types of assets should be distributed
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <p className="text-stone-700 font-bold">
                    Review and confirm your choices in one clear summary
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-cyan-900 mb-3">Why This Matters</h2>
            <ul className="space-y-2 text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Helps prevent confusion or disputes among family members</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Ensures your assets go to the people or causes you intend</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Provides clarity and direction for your executor during settlement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Reduces stress and decision-making burden on loved ones</span>
              </li>
            </ul>
          </div>

          {/* Estimated Time */}
          <div className="text-center text-stone-600">
            <p className="text-sm">
              <span className="font-semibold">Estimated time:</span> ~15–30 minutes
            </p>
            <p className="text-xs mt-1">
              Time may vary depending on the number and complexity of assets.
            </p>
          </div>

          {/* Important Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-xl">💡</div>
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Important Note</h3>
                <p className="text-sm text-yellow-800">
                  This tool helps you document your wishes, but it does not create legally binding
                  documents. For legal validity, work with an attorney to create or update your
                  will, trust, or other estate planning documents that reflect these assignments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Assign and Approve Asset Distribution"
        subtitle="Decide how your assets should be distributed"
        progressPercent={0}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Get Started"
        backText="Back to Dashboard"
      />
    </ResponsiveContainer>
  );
};

export default Frame245;